"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Check, ChevronDown, MapPin, Plus, Search, X } from "lucide-react";
import { fetchPriceList, summarise, type AvailabilitySummary } from "@/lib/v2/availability";
import { formatArea, formatPriceShort, formatPsf, sanePrice, statusMeta, tenureLabel, topYear } from "@/lib/v2/format";
import { propertyCoords, propertyHref, propertyImage, type PropertyLite } from "@/lib/v2/property";
import { WHATSAPP_NUMBER } from "@/lib/v2/constants";
import { DISTRICTS } from "@/lib/districts";
import { cn, formatPropertyName } from "@/lib/utils";
import { Button } from "../Button";
import { Pill } from "../Pill";

export const MAX_PROJECTS = 4;

const TYPE_ORDER = ["Studio", "1 Bedroom", "2 Bedroom", "3 Bedroom", "4 Bedroom", "5 Bedroom", "Penthouse", "Other"];

/** Roll a raw unit type ("2 Bedroom + Study Premium") up to its bedroom bucket. */
function bucketOf(type: string): string {
  const t = type.toLowerCase();
  if (/penthouse/.test(t)) return "Penthouse";
  if (/studio/.test(t)) return "Studio";
  const m = t.match(/(\d)\s*(?:bed|br)/);
  if (m) return `${m[1]} Bedroom`;
  return "Other";
}

function km(a: { lat: number; lng: number } | null, b: { lat: number; lng: number } | null): number | null {
  if (!a || !b) return null;
  const r = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

function displayName(p: PropertyLite) {
  return (p.title || p.name).trim();
}

function districtLine(p: PropertyLite) {
  const label = p.district ? DISTRICTS[p.district]?.label : undefined;
  return [p.district, label].filter(Boolean).join(" · ");
}

type Column = {
  p: PropertyLite;
  slug: string;
  summary: AvailabilitySummary | null;
  loading: boolean;
};

const NotListed = () => <span className="text-ink-faint">Not listed</span>;
const Skeleton = () => <span className="inline-block h-4 w-20 animate-pulse rounded bg-surface" />;

/**
 * Full compare tool: up to four projects against each other, first at project
 * level, then per bedroom type with live prices, PSF, sizes and units left.
 * Selection lives in the URL (?projects=slug,slug) so a comparison can be shared.
 */
export function CompareTool({ all }: { all: PropertyLite[] }) {
  const router = useRouter();
  const params = useSearchParams();

  const bySlug = useMemo(() => {
    const m = new Map<string, PropertyLite>();
    all.forEach((p) => m.set(formatPropertyName(p.name), p));
    return m;
  }, [all]);

  const [slugs, setSlugs] = useState<string[]>(() =>
    (params.get("projects") ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s && bySlug.has(s))
      .slice(0, MAX_PROJECTS),
  );
  const [types, setTypes] = useState<string[] | null>(null); // null = all
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Keep the URL in step with the picks so the page can be shared or reloaded.
  useEffect(() => {
    const next = slugs.length ? `?projects=${slugs.join(",")}` : "";
    const current = params.get("projects") ? `?projects=${params.get("projects")}` : "";
    if (next !== current) router.replace(`/compare${next}`, { scroll: false });
  }, [slugs, params, router]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const picked = slugs.map((s) => bySlug.get(s)!).filter(Boolean);
  const pickedIds = new Set(picked.map((p) => p._id));

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = all.filter((p) => !pickedIds.has(p._id));
    if (!q) return pool.slice(0, 8);
    return pool
      .filter((p) => displayName(p).toLowerCase().includes(q) || (p.district ?? "").toLowerCase() === q || districtLine(p).toLowerCase().includes(q))
      .slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, all, slugs]);

  const suggestions = useMemo(() => {
    const anchor = picked[0];
    if (!anchor) return [];
    const origin = propertyCoords(anchor);
    return all
      .filter((p) => !pickedIds.has(p._id))
      .map((p) => ({ p, d: km(origin, propertyCoords(p)) }))
      .filter((x) => x.d != null)
      .sort((a, b) => a.d! - b.d!)
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, slugs]);

  const unitQueries = useQueries({
    queries: picked.map((p) => ({
      queryKey: ["v2-compare-units", p._id],
      queryFn: () => fetchPriceList(p._id),
      staleTime: 5 * 60_000,
    })),
  });

  const columns: Column[] = picked.map((p, i) => ({
    p,
    slug: slugs[i],
    summary: unitQueries[i]?.data ? summarise(unitQueries[i].data!) : null,
    loading: unitQueries[i]?.isLoading ?? false,
  }));

  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    columns.forEach((c) => c.summary?.byType.forEach((t) => set.add(bucketOf(t.type))));
    return TYPE_ORDER.filter((t) => set.has(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.map((c) => c.summary).join("|")]);

  const activeTypes = types ? types.filter((t) => availableTypes.includes(t)) : availableTypes;

  const add = (p: PropertyLite) => {
    const slug = formatPropertyName(p.name);
    setSlugs((cur) => (cur.includes(slug) || cur.length >= MAX_PROJECTS ? cur : [...cur, slug]));
    setQuery("");
    setOpen(false);
  };
  const remove = (slug: string) => setSlugs((cur) => cur.filter((s) => s !== slug));
  const toggleType = (t: string) =>
    setTypes((cur) => {
      const base = cur ?? availableTypes;
      return base.includes(t) ? base.filter((x) => x !== t) : [...base, t];
    });

  const full = slugs.length >= MAX_PROJECTS;
  const waMessage = encodeURIComponent(
    picked.length
      ? `Hi Winnie, I am comparing ${picked.map(displayName).join(", ")}. Which unit would you suggest for me?`
      : "Hi Winnie, I would like help comparing a few new launches.",
  );

  const overviewRows: Array<{ label: string; render: (c: Column) => React.ReactNode }> = [
    {
      label: "Stage",
      render: (c) => {
        const s = statusMeta(c.p.construction_status || "Upcoming", c.p.isPreLaunch);
        return <Pill tone={s.tone}>{s.label}</Pill>;
      },
    },
    { label: "District", render: (c) => districtLine(c.p) || null },
    { label: "Tenure", render: (c) => tenureLabel(!!c.p.is_leasehold, c.p.lease_year) },
    { label: "Expected TOP", render: (c) => topYear(c.p.expected_top) },
    { label: "Total units", render: (c) => (c.p.units ? c.p.units.toLocaleString("en-SG") : c.summary?.total || null) },
    {
      label: "Units available",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const s = c.summary;
        if (!s || s.total === 0) return <span className="text-ink-faint">Not released</span>;
        return (
          <span className="font-semibold text-avail">
            {s.available} of {s.total}
          </span>
        );
      },
    },
    {
      label: "From",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const v = c.summary?.minPrice || sanePrice(c.p.min_flat_price);
        return v ? <span className="font-semibold text-gold-600">{formatPriceShort(v)}</span> : null;
      },
    },
    {
      label: "PSF from",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const v = c.summary?.minPsf || c.p.psf_from;
        return v ? formatPsf(v) : null;
      },
    },
  ];

  return (
    <div className="space-y-10">
      {/* Step 1: projects */}
      <section className="rounded-3xl border border-line bg-white p-5 shadow-card md:p-7">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-700">Step 1</p>
            <h2 className="mt-1 font-display text-xl font-semibold text-ink md:text-2xl">Pick up to four projects</h2>
          </div>
          <p className="text-sm text-ink-muted">
            {picked.length} of {MAX_PROJECTS} chosen
          </p>
        </div>

        <div ref={searchRef} className="relative mt-5">
          <label htmlFor="compare-search" className="sr-only">
            Search a project or district
          </label>
          <div className={cn("flex items-center gap-3 rounded-2xl border bg-white px-4", open ? "border-brand-500 ring-2 ring-brand-500/20" : "border-line")}>
            <Search aria-hidden="true" className="size-5 shrink-0 text-ink-faint" />
            <input
              id="compare-search"
              value={query}
              disabled={full}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={full ? "Four projects chosen. Remove one to add another." : "Type a project name or district, like Lentor or D19"}
              className="h-12 w-full bg-transparent text-base text-ink outline-none placeholder:text-ink-faint disabled:cursor-not-allowed"
              autoComplete="off"
            />
            <ChevronDown aria-hidden="true" className={cn("size-4 text-ink-faint transition-transform", open && "rotate-180")} />
          </div>
          {open && !full && matches.length > 0 && (
            <ul className="absolute left-0 right-0 top-full z-30 mt-2 max-h-96 overflow-auto rounded-2xl border border-line bg-white p-2 shadow-card">
              {matches.map((p) => {
                const img = propertyImage(p);
                const s = statusMeta(p.construction_status || "Upcoming", p.isPreLaunch);
                return (
                  <li key={p._id}>
                    <button type="button" onClick={() => add(p)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-surface">
                      <span className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-surface">
                        {img && <Image src={img} alt="" fill unoptimized sizes="48px" className="object-cover" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-ink">{displayName(p)}</span>
                        <span className="block truncate text-xs text-ink-muted">{districtLine(p)}</span>
                      </span>
                      <Pill tone={s.tone}>{s.label}</Pill>
                      <Plus aria-hidden="true" className="size-4 text-ink-faint" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {picked.map((p, i) => {
            const img = propertyImage(p);
            return (
              <div key={p._id} className="relative overflow-hidden rounded-2xl border border-line bg-white">
                <div className="relative aspect-[16/10] bg-surface">
                  {img ? <Image src={img} alt={displayName(p)} fill unoptimized sizes="(min-width: 1024px) 25vw, 50vw" className="object-cover" /> : null}
                  <button
                    type="button"
                    onClick={() => remove(slugs[i])}
                    aria-label={`Remove ${displayName(p)}`}
                    className="absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card hover:bg-white"
                  >
                    <X aria-hidden="true" className="size-4" />
                  </button>
                </div>
                <div className="p-3">
                  <p className="truncate font-display font-semibold text-ink">{displayName(p)}</p>
                  <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-ink-muted">
                    <MapPin aria-hidden="true" className="size-3" />
                    {districtLine(p) || "District not listed"}
                  </p>
                </div>
              </div>
            );
          })}
          {Array.from({ length: MAX_PROJECTS - picked.length }).map((_, i) => (
            <button
              key={`empty-${i}`}
              type="button"
              onClick={() => document.getElementById("compare-search")?.focus()}
              className="flex min-h-[172px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-line text-sm font-medium text-ink-faint hover:border-brand-500 hover:text-brand-700"
            >
              <Plus aria-hidden="true" className="size-5" />
              Add a project
            </button>
          ))}
        </div>

        {suggestions.length > 0 && !full && (
          <div className="mt-5">
            <p className="text-sm text-ink-muted">Near {displayName(picked[0])}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {suggestions.map(({ p, d }) => (
                <button
                  key={p._id}
                  type="button"
                  onClick={() => add(p)}
                  className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-2 text-sm font-medium text-ink hover:bg-surface"
                >
                  <Plus aria-hidden="true" className="size-4 text-ink-faint" />
                  {displayName(p)}
                  <span className="text-xs text-ink-muted">{d!.toFixed(1)} km</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Step 2: unit types */}
      {picked.length > 0 && (
        <section className="rounded-3xl border border-line bg-white p-5 shadow-card md:p-7">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-700">Step 2</p>
          <h2 className="mt-1 font-display text-xl font-semibold text-ink md:text-2xl">Choose the unit types to compare</h2>
          {availableTypes.length === 0 ? (
            <p className="mt-3 text-sm text-ink-muted">{columns.some((c) => c.loading) ? "Loading unit lists…" : "No unit list is released yet for the projects you picked."}</p>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTypes(null)}
                aria-pressed={types === null}
                className={cn(
                  "rounded-full border px-3.5 py-2 text-sm font-medium transition-colors",
                  types === null ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-surface",
                )}
              >
                All types
              </button>
              {availableTypes.map((t) => {
                const on = activeTypes.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    aria-pressed={on}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors",
                      on ? "border-brand-500 bg-brand-50 text-brand-700" : "border-line bg-white text-ink hover:bg-surface",
                    )}
                  >
                    {on ? <Check aria-hidden="true" className="size-4" /> : <Plus aria-hidden="true" className="size-4" />}
                    {t}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Results */}
      {picked.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-line bg-surface px-6 py-14 text-center">
          <p className="font-display text-lg font-semibold text-ink">Your comparison shows up here</p>
          <p className="mt-2 text-sm text-ink-muted">Add at least one project above. Two or more gives you the side-by-side view.</p>
        </div>
      ) : (
        <>
          <CompareTable title="Project overview" columns={columns} rows={overviewRows} />

          {activeTypes.map((bucket) => (
            <CompareTable
              key={bucket}
              title={bucket === "Other" ? "Other unit types" : `${bucket} units`}
              columns={columns}
              rows={typeRows(bucket)}
            />
          ))}

          <section className="rounded-3xl bg-ink px-6 py-8 text-white md:flex md:items-center md:justify-between md:px-10">
            <div>
              <h2 className="font-display text-xl font-semibold md:text-2xl">Not sure which unit fits?</h2>
              <p className="mt-2 text-sm text-white/70">Send Winnie the projects you are comparing. She replies with the units worth viewing and the ones to skip.</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3 md:mt-0">
              <Button variant="whatsapp" href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMessage}`} target="_blank" rel="noopener noreferrer">
                WhatsApp Winnie
              </Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/** Rows for one bedroom bucket: variants, units left, price and size ranges, links. */
function typeRows(bucket: string): Array<{ label: string; render: (c: Column) => React.ReactNode }> {
  const pick = (c: Column) => c.summary?.byType.filter((t) => bucketOf(t.type) === bucket) ?? [];
  const nums = (xs: Array<number | null>) => xs.filter((n): n is number => n != null && n > 0);
  return [
    {
      label: "Variants",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const rows = pick(c);
        if (rows.length === 0) return <span className="text-ink-faint">None in this project</span>;
        return (
          <ul className="space-y-1">
            {rows.map((r) => (
              <li key={r.type} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-ink">{r.type}</span>
                <span className={cn("shrink-0 text-xs font-medium", r.available > 0 ? "text-avail" : "text-ink-faint")}>
                  {r.available > 0 ? `${r.available} left` : "Sold out"}
                </span>
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      label: "Units left",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const rows = pick(c);
        if (rows.length === 0) return null;
        const avail = rows.reduce((n, r) => n + r.available, 0);
        const total = rows.reduce((n, r) => n + r.total, 0);
        return avail > 0 ? (
          <span className="font-semibold text-avail">
            {avail} of {total}
          </span>
        ) : (
          <span className="font-semibold text-sold">Sold out</span>
        );
      },
    },
    {
      label: "Price range",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const rows = pick(c);
        if (rows.length === 0) return null;
        const lo = nums(rows.map((r) => r.minPrice));
        const hi = nums(rows.map((r) => r.maxPrice));
        if (!lo.length) return <span className="text-ink-faint">Price on request</span>;
        return (
          <span className="font-semibold text-gold-600">
            {formatPriceShort(Math.min(...lo))}
            {hi.length && Math.max(...hi) > Math.min(...lo) ? ` to ${formatPriceShort(Math.max(...hi))}` : ""}
          </span>
        );
      },
    },
    {
      label: "PSF range",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const rows = pick(c);
        if (rows.length === 0) return null;
        const lo = nums(rows.map((r) => r.minPsf));
        const hi = nums(rows.map((r) => r.maxPsf));
        if (!lo.length) return null;
        return `${formatPsf(Math.min(...lo))}${hi.length && Math.max(...hi) > Math.min(...lo) ? ` to ${formatPsf(Math.max(...hi))}` : ""}`;
      },
    },
    {
      label: "Size",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const rows = pick(c);
        if (rows.length === 0) return null;
        const lo = Math.min(...rows.map((r) => r.minArea));
        const hi = Math.max(...rows.map((r) => r.maxArea));
        return hi > lo ? `${formatArea(lo)} to ${formatArea(hi)}` : formatArea(lo);
      },
    },
    {
      label: "Floor plans",
      render: (c) => {
        if (c.loading) return <Skeleton />;
        const rows = pick(c);
        if (rows.length === 0) return null;
        const plans = c.summary?.byPlan.filter((p) => bucketOf(p.type) === bucket).length ?? 0;
        return (
          <Link href={`${propertyHref(c.p)}#floor-plans`} className="font-medium text-brand-700 hover:underline">
            {plans > 0 ? `See ${plans} floor plan${plans === 1 ? "" : "s"}` : "See floor plans"}
          </Link>
        );
      },
    },
  ];
}

function CompareTable({ title, columns, rows }: { title: string; columns: Column[]; rows: Array<{ label: string; render: (c: Column) => React.ReactNode }> }) {
  return (
    <section>
      <h2 className="mb-4 font-display text-xl font-semibold text-ink md:text-2xl">{title}</h2>
      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full border-collapse text-left" style={{ minWidth: `${200 + columns.length * 220}px` }}>
          <thead>
            <tr className="border-b border-line bg-surface">
              <th scope="col" className="w-44 px-4 py-3" />
              {columns.map((c) => {
                const img = propertyImage(c.p);
                return (
                  <th key={c.p._id} scope="col" className="px-4 py-3 align-top">
                    <Link href={propertyHref(c.p)} className="flex items-center gap-3">
                      <span className="relative size-10 shrink-0 overflow-hidden rounded-lg bg-white">
                        {img && <Image src={img} alt="" fill unoptimized sizes="40px" className="object-cover" />}
                      </span>
                      <span className="font-display text-base font-semibold text-ink hover:text-brand-700">{displayName(c.p)}</span>
                    </Link>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-line last:border-b-0">
                <th scope="row" className="px-4 py-3 text-left align-top text-xs font-semibold uppercase tracking-wide text-ink-faint">
                  {r.label}
                </th>
                {columns.map((c) => (
                  <td key={`${r.label}-${c.p._id}`} className="px-4 py-3 align-top text-sm text-ink">
                    {r.render(c) ?? <NotListed />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
